import { Alert, Button, Drawer, Group, PasswordInput, Radio, Select, Stack, Switch, Text, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  defaultDepartmentForRole,
  MIN_PASSWORD_LENGTH,
  PRIVILEGED_ROLES,
  ROLE_LABELS,
  ROLES,
  USER_STATUSES,
  type CreatedUserDto,
  type Role,
  type UserDto,
  type UserStatus,
} from '@mexp/shared';
import { errorMessage, http } from '@/api/client';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useDepartments, useExporterLookup } from '@/features/shared/lookups';

const STATUS_LABELS: Record<UserStatus, string> = { active: 'Active', inactive: 'Inactive', suspended: 'Suspended', pending_invite: 'Invited' };

interface Values {
  email: string;
  fullName: string;
  role: Role | '';
  department: string | null;
  exporterId: string | null;
  designation: string;
  phone: string;
  notes: string;
  status: UserStatus;
  passwordMode: 'temporary' | 'set';
  password: string;
  requirePasswordChange: boolean;
}

export function UserDrawer({
  opened,
  onClose,
  user,
  presetExporterId,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  user?: UserDto | null;
  presetExporterId?: string;
  onCreated?: (result: CreatedUserDto) => void;
}) {
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  const editing = !!user;
  const [error, setError] = useState<string | null>(null);
  const departments = useDepartments();
  const exporters = useExporterLookup(opened);

  const form = useForm<Values>({
    initialValues: {
      email: '',
      fullName: '',
      role: presetExporterId ? 'exporter' : '',
      department: null,
      exporterId: presetExporterId ?? null,
      designation: '',
      phone: '',
      notes: '',
      status: 'active',
      passwordMode: 'temporary',
      password: '',
      requirePasswordChange: true,
    },
    validate: {
      email: (v) => (editing || /^\S+@\S+\.\S+$/.test(v.trim()) ? null : 'Enter a valid email'),
      fullName: (v) => (v.trim().length >= 2 ? null : 'Enter the full name'),
      role: (v) => (v ? null : 'Choose a role'),
      exporterId: (v, values) => (values.role === 'exporter' && !v ? 'Choose the exporter company' : null),
      password: (v, values) =>
        !editing && values.passwordMode === 'set' && v.length < MIN_PASSWORD_LENGTH ? `Use at least ${MIN_PASSWORD_LENGTH} characters` : null,
    },
  });

  useEffect(() => {
    if (!opened) return;
    setError(null);
    if (user) {
      form.setValues({
        email: user.email,
        fullName: user.fullName,
        role: user.role,
        department: user.department,
        exporterId: user.exporterId,
        designation: user.designation ?? '',
        phone: user.phone ?? '',
        notes: user.notes ?? '',
        status: user.status,
      });
    } else {
      form.reset();
      if (presetExporterId) form.setValues({ role: 'exporter', exporterId: presetExporterId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, user, presetExporterId]);

  const canManagePrivileged = me.role === 'super_admin';
  const roleOptions = ROLES.filter((r) => canManagePrivileged || !(PRIVILEGED_ROLES as readonly Role[]).includes(r)).map((r) => ({
    value: r,
    label: ROLE_LABELS[r],
  }));
  const isSelf = user?.id === me.id;
  const isExporter = form.values.role === 'exporter';

  const save = useMutation({
    mutationFn: async (values: Values) => {
      const common = {
        fullName: values.fullName.trim(),
        role: values.role as Role,
        department: isExporter ? undefined : values.department ?? undefined,
        exporterId: isExporter ? values.exporterId ?? undefined : undefined,
      };
      if (editing && user) {
        return http.patch<UserDto>(`/users/${user.id}`, {
          ...common,
          department: isExporter ? null : values.department,
          designation: values.designation,
          phone: values.phone,
          notes: values.notes,
          status: values.status,
        });
      }
      return http.post<CreatedUserDto>('/users', {
        ...common,
        email: values.email.trim(),
        designation: values.designation || undefined,
        phone: values.phone || undefined,
        notes: values.notes || undefined,
        password: values.passwordMode === 'set' ? values.password : undefined,
        requirePasswordChange: values.passwordMode === 'set' ? values.requirePasswordChange : true,
      });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['exporters'] });
      if (!editing) onCreated?.(result as CreatedUserDto);
      onClose();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Drawer opened={opened} onClose={onClose} title={editing ? `Edit ${user?.fullName}` : 'New user'}>
      <form onSubmit={form.onSubmit((v) => save.mutate(v))} noValidate>
        <Stack gap="md">
          {error && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
              {error}
            </Alert>
          )}
          <TextInput label="Email" description={editing ? 'The sign-in email cannot be changed.' : undefined} disabled={editing} withAsterisk {...form.getInputProps('email')} />
          <TextInput label="Full name" withAsterisk {...form.getInputProps('fullName')} />
          <Select
            label="Role"
            withAsterisk
            data={roleOptions}
            disabled={isSelf}
            description={isSelf ? 'You cannot change your own role.' : undefined}
            searchable
            {...form.getInputProps('role')}
            onChange={(value) => {
              form.setFieldValue('role', (value ?? '') as Role);
              if (value && value !== 'exporter' && !form.values.department) form.setFieldValue('department', defaultDepartmentForRole(value as Role));
            }}
          />
          {isExporter ? (
            <Select
              label="Exporter company"
              withAsterisk
              searchable
              data={(exporters.data ?? []).map((e) => ({ value: e.id, label: `${e.companyName} (${e.code})` }))}
              nothingFoundMessage="No matching exporter"
              {...form.getInputProps('exporterId')}
            />
          ) : (
            <Select
              label="Department"
              searchable
              clearable
              data={(departments.data ?? []).map((d) => ({ value: d.code, label: d.name }))}
              {...form.getInputProps('department')}
            />
          )}
          <Group grow>
            <TextInput label="Designation" {...form.getInputProps('designation')} />
            <TextInput label="Phone" {...form.getInputProps('phone')} />
          </Group>
          {editing && (
            <Select
              label="Status"
              data={USER_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
              disabled={isSelf}
              description="Inactive and suspended users cannot sign in; their sessions end immediately."
              {...form.getInputProps('status')}
            />
          )}
          <Textarea label="Notes" autosize minRows={2} maxRows={5} {...form.getInputProps('notes')} />
          {!editing && (
            <Stack gap="xs">
              <Radio.Group label="Password" {...form.getInputProps('passwordMode')}>
                <Stack gap={6} mt={6}>
                  <Radio value="temporary" label="Generate a temporary password (they choose their own at first sign-in)" />
                  <Radio value="set" label="Set a password now" />
                </Stack>
              </Radio.Group>
              {form.values.passwordMode === 'set' && (
                <>
                  <PasswordInput label="Password" autoComplete="new-password" {...form.getInputProps('password')} />
                  <Switch label="Ask them to change it at first sign-in" {...form.getInputProps('requirePasswordChange', { type: 'checkbox' })} />
                </>
              )}
            </Stack>
          )}
          <Text fz="xs" c="dimmed">
            Every change is recorded in the activity log.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              {editing ? 'Save changes' : 'Create user'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
